import { ArrowRightLeft, BadgeCheck, Landmark, Wallet } from "lucide-react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

const flowCards = [
  { title: "Customer pays", detail: "Supported digital value", icon: Wallet },
  { title: "Koineckta connects", detail: "Payment orchestration", icon: ArrowRightLeft },
  { title: "Merchant receives", detail: "Familiar local currency", icon: Landmark },
];

export default function HeroVisual() {
  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);
  const rotateX = useSpring(useTransform(pointerY, [-90, 90], [10, -10]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(pointerX, [-90, 90], [-12, 12]), { stiffness: 120, damping: 18 });

  const handlePointerMove = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    pointerX.set(event.clientX - bounds.left - bounds.width / 2);
    pointerY.set(event.clientY - bounds.top - bounds.height / 2);
  };

  const resetPointer = () => {
    pointerX.set(0);
    pointerY.set(0);
  };

  return (
    <div className="relative mx-auto w-full max-w-2xl" data-testid="hero-visual-panel">
      <div className="hero-orb hero-orb-left" />
      <div className="hero-orb hero-orb-right" />
      <motion.div
        className="relative overflow-hidden rounded-[32px] border border-[#3ABDBC]/25 bg-[#08133E]/88 p-6 shadow-[0_30px_90px_-30px_rgba(58,189,188,0.38)] backdrop-blur-xl sm:p-8"
        style={{ perspective: 1600, rotateX, rotateY }}
        onMouseMove={handlePointerMove}
        onMouseLeave={resetPointer}
        initial={{ opacity: 0, y: 50, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(120deg,rgba(58,189,188,0.12),transparent_38%),linear-gradient(300deg,rgba(106,227,47,0.08),transparent_32%)]" />
        <div className="mb-6 flex items-center justify-between gap-4 rounded-3xl border border-[#3ABDBC]/25 bg-[#010B30] px-5 py-4 text-white shadow-lg" data-testid="hero-visual-summary-card">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-sky-200">Payment layer preview</p>
            <p className="mt-2 font-display text-2xl font-semibold">Digital value. Everyday payments.</p>
          </div>
          <div className="rounded-full bg-white/10 p-3">
            <BadgeCheck className="h-8 w-8 text-[#6AE32F]" />
          </div>
        </div>

        <div className="mb-6 overflow-hidden rounded-[28px] border border-[#3ABDBC]/20 bg-[#0B1845] p-4 shadow-[0_24px_70px_-40px_rgba(1,11,48,0.8)]" data-testid="hero-payment-preview-frame">
          <motion.div className="aspect-[16/10] rounded-[24px] bg-[#010B30] p-5 sm:p-6" initial={{ opacity: 0.7, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}>
            <div className="flex items-start justify-between gap-4 border-b border-[#303B67] pb-4">
              <div><p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#45D6D1]">Merchant payment request</p><p className="mt-2 font-display text-xl font-semibold text-white">Everyday Store</p></div>
              <div className="text-right"><p className="text-xs text-[#9BA8C6]">Customer total</p><p className="mt-1 font-display text-2xl font-semibold text-white">₦50,000</p></div>
            </div>
            <div className="grid gap-3 py-5 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
              <div className="rounded-2xl border border-[#303B67] bg-[#08133E] p-4"><Wallet className="h-5 w-5 text-[#45D6D1]" /><p className="mt-3 text-xs text-[#9BA8C6]">Customer funds with</p><p className="mt-1 text-sm font-semibold text-white">Supported digital asset</p></div>
              <ArrowRightLeft className="mx-auto h-5 w-5 rotate-90 text-[#6AE32F] sm:rotate-0" />
              <div className="rounded-2xl border border-[#6AE32F]/30 bg-[#6AE32F]/8 p-4"><Landmark className="h-5 w-5 text-[#6AE32F]" /><p className="mt-3 text-xs text-[#9BA8C6]">Merchant receives</p><p className="mt-1 text-sm font-semibold text-white">Local currency settlement</p></div>
            </div>
            <div className="flex items-center justify-between gap-3 rounded-xl bg-[#121E4E] px-4 py-3"><span className="text-xs font-medium text-[#B8C4DC]">Koineckta manages the payment bridge</span><BadgeCheck className="h-5 w-5 shrink-0 text-[#6AE32F]" /></div>
          </motion.div>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          {flowCards.map(({ title, detail, icon: Icon }) => (
            <div
              key={title}
              className="rounded-3xl border border-[#3ABDBC]/20 bg-[#0B1845]/90 p-5 shadow-[0_18px_40px_-24px_rgba(1,11,48,0.65)]"
              data-testid={`hero-flow-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <div className="mb-4 inline-flex rounded-2xl bg-[#3ABDBC]/12 p-3 text-[#45D6D1]">
                <Icon className="h-5 w-5" />
              </div>
              <p className="font-display text-lg font-semibold text-slate-950">{title}</p>
              <p className="mt-2 text-sm leading-6 text-slate-600">{detail}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-3xl bg-gradient-to-r from-[#3ABDBC] via-[#00F0FF] to-[#6AE32F] p-[1px]" data-testid="hero-visual-timeline-card">
          <div className="rounded-[calc(1.5rem-1px)] bg-[#08133E] px-5 py-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-slate-500">Launch path</p>
                <p className="mt-2 text-sm font-medium text-slate-700">Merchant pilot → Payment acceptance → African commerce</p>
              </div>
              <span className="rounded-full bg-[#6AE32F]/12 px-4 py-2 text-sm font-semibold text-[#6AE32F]" data-testid="hero-launch-badge">
                Nigeria to Africa
              </span>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}